import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoImageOutline, IoAttach } from "react-icons/io5";
import { SettingsContext } from "../contexts/SettingsContext";
import { ConversationsContext } from "../contexts/ConversationsContext";
import { motion, AnimatePresence } from "framer-motion";
import { useFileUpload } from "../utils/useFileUpload";
import Modal from "../components/Modal";
import { useToast } from "../contexts/ToastContext";
import InputContainer from "../components/InputContainer";
import "../styles/Common.css";

function Home({ isTouch, userInfo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState("");
  const [noticeHash, setNoticeHash] = useState("");
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showNotice, setShowNotice] = useState(false);

  const { showToast } = useToast();

  const abortControllerRef = useRef(null);

  const welcomeMessage = useMemo(() => {
    const h = new Date().getHours();
    const name = userInfo?.name?.split(' ')[0];

    const morning = [
      `좋은 아침이에요, ${name}님.`,
      "오늘 아침도 시작해 볼까요?",
      "오늘 하루도 잘 부탁드려요.",
      "오늘 아침엔 무엇이 궁금하신가요?",
    ];
    const afternoon = [
      "오후의 대화를 시작해 볼까요?",
      `즐거운 오후입니다, ${name}님!`,
      "오후엔 어떤 주제로 이야기할까요?",
    ];
    const evening = [
      "오늘 하루 수고 많으셨어요.",
      "벌써 하루가 끝나가네요.",
      "오늘 저녁엔 무얼 하실 계획이신가요?",
    ];
    const night = [
      `늦은 밤이네요, ${name}님.`,
      "오늘 밤엔 어떤 아이디어가 떠오르나요?",
      "오늘 밤엔 어떤 걸 알아볼까요?",
    ];
    const general = [
      `반갑습니다, ${name}님.`,
      `오늘도 잘 부탁드려요, ${name}님.`,
      "오늘은 무엇을 도와드릴까요?",
      "어디서부터 시작할까요?",
      "새로운 대화를 시작해 볼까요?",
      "오늘은 어떤 게 궁금하신가요?",
      "무엇을 알아볼까요?",
      "대화를 시작해 볼까요?",
      `잘 오셨어요, ${name}님.`,
    ];

    let pool;
    if (h >= 5 && h < 12) pool = morning;
    else if (h >= 12 && h < 17) pool = afternoon;
    else if (h >= 17 && h < 21) pool = evening;
    else pool = night;

    const combined = [...pool, ...general];
    return combined[Math.floor(Math.random() * combined.length)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    uploadedFiles,
    processFiles,
    removeFile
  } = useFileUpload([], userInfo, "chat");

  const { models, model, canVision, setHasImage } = useContext(SettingsContext);
  const { addConversation } = useContext(ConversationsContext);

  const uploadingFiles = uploadedFiles.some((file) => !file.content);

  useEffect(() => {
    const fetchNotice = async () => {
      try {
        const noticeRes = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/notice`, { credentials: "include" });
        if (!noticeRes.ok) {
          return;
        }
        const { message, hash } = await noticeRes.json();
        setNotice(message);
        setNoticeHash(hash);
        
        const storedHash = localStorage.getItem('noticeHash');
        if (hash && (!storedHash || storedHash !== hash)) {
          setShowNotice(true);
        }
      } catch (error) {}
    };
    
    fetchNotice();
  }, []);

  useEffect(() => {
    if (location.state?.errorModal) {
      showToast(location.state.errorModal);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showToast]);

  const sendMessage = useCallback(
    async (message) => {
      if (!message.trim() || uploadingFiles) return;
      try {
        const selectedModel = models.find((m) => m.model_name === model);
        if (!selectedModel) {
          throw new Error("선택한 모델이 유효하지 않습니다.");
        }
        setIsLoading(true);
        
        const controller = new AbortController();
        abortControllerRef.current = controller;
        
        const res = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/chat/new_conversation`, {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({})
        });
        if (!res.ok) {
          throw new Error('새 대화를 시작하는 데 실패했습니다.');
        }
        
        const data = await res.json();
        const newConversation = {
          type: "chat",
          conversation_id: data.conversation_id,
          alias: "새 대화",
          starred: false,
          starred_at: null,
          created_at: data.created_at,
          updated_at: data.updated_at,
          isLoading: true
        };
        addConversation(newConversation);

        navigate(`/chat/${data.conversation_id}`, {
          state: {
            initialMessage: message,
            initialFiles: uploadedFiles,
          },
          replace: false,
        });
      } catch (error) {
        showToast("새 대화를 시작하는 데 실패했습니다.");
        setIsLoading(false);
      } finally {
        abortControllerRef.current = null;
      }
    },
    [
      models,
      model,
      navigate,
      uploadedFiles,
      uploadingFiles,
      addConversation,
      showToast
    ]
  );

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);
  
  useEffect(() => {
    const hasUploadedImage = uploadedFiles.some((file) => {
      return (file.type && (file.type === "image" || file.type.startsWith("image/"))) || 
        /\.(jpe?g|png|gif|bmp|webp)$/i.test(file.name);
    });
    setHasImage(hasUploadedImage);
  },
  [setHasImage, uploadedFiles]);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setIsDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      await processFiles(files);
    },
    [processFiles]
  );

  return (
    <div
      className="container"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="welcome-container">
      <motion.div
          className="welcome-message"
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {welcomeMessage}
        </motion.div>
      </div>

      <InputContainer
        isTouch={isTouch}
        placeholder="내용 입력"
        inputText={inputText}
        setInputText={setInputText}
        isLoading={isLoading}
        onSend={sendMessage}
        onCancel={cancelRequest}
        uploadedFiles={uploadedFiles}
        processFiles={processFiles}
        removeFile={removeFile}
        uploadingFiles={uploadingFiles}
      />

      <AnimatePresence>
        {isDragActive && (
          <motion.div
            key="drag-overlay"
            className="drag-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            <div className="drag-container">
              {canVision ? (
                <>
                  <IoImageOutline style={{ fontSize: "40px" }} />
                  <div className="drag-text">여기에 파일 또는 이미지를 추가하세요</div>
                </>
              ) : (
                <>
                  <IoAttach style={{ fontSize: "40px" }} />
                  <div className="drag-text">여기에 파일을 추가하세요</div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNotice && (
          <Modal
            message={notice}
            onConfirm={() => {
              localStorage.setItem('noticeHash', noticeHash);
              setShowNotice(false);
            }}
            showCancelButton={false}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default React.memo(Home);
